import { useState, useEffect, useCallback } from 'react'
import {
  type ConsentFlags,
  type ConsentState,
  type ConsentChoice,
  DEFAULT_FLAGS,
  ALL_FLAGS,
  loadConsentFromStorage,
  saveConsentToStorage,
  syncConsentWithDB,
  loadConsentFromDB,
} from '@/src/lib/consent'

interface UseConsentReturn {
  consentState: ConsentState | null
  /** true mientras se lee localStorage (evita flash del banner) */
  isLoading: boolean
  /** El banner debe mostrarse si no hay elección previa */
  showBanner: boolean
  acceptAll: () => Promise<void>
  rejectAll: () => Promise<void>
  saveCustom: (flags: Partial<ConsentFlags>) => Promise<void>
  hasConsent: (category: keyof ConsentFlags) => boolean
  resetConsent: () => void
}

export function useConsent(): UseConsentReturn {
  const [consentState, setConsentState] = useState<ConsentState | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  // Carga inicial: primero localStorage (síncrono → sin flash), luego DB
  useEffect(() => {
    const stored = loadConsentFromStorage()
    if (stored) {
      setConsentState(stored)
      setIsLoading(false)
      // Intenta refrescar desde DB en segundo plano
      loadConsentFromDB().then((dbFlags) => {
        if (dbFlags) {
          const merged: ConsentState = {
            ...stored,
            flags: dbFlags,
            updatedAt: new Date().toISOString(),
          }
          setConsentState(merged)
          saveConsentToStorage(merged)
        }
      })
    } else {
      setIsLoading(false)
    }
  }, [])

  const persist = useCallback((choice: ConsentChoice, flags: ConsentFlags) => {
    const state: ConsentState = {
      choice,
      flags,
      updatedAt: new Date().toISOString(),
    }
    setConsentState(state)
    saveConsentToStorage(state)
    // Sincroniza en background, no bloqueante
    syncConsentWithDB(flags)
  }, [])

  const acceptAll = useCallback(async () => {
    persist('accepted', ALL_FLAGS)
  }, [persist])

  const rejectAll = useCallback(async () => {
    persist('rejected', DEFAULT_FLAGS)
  }, [persist])

  const saveCustom = useCallback(async (flags: Partial<ConsentFlags>) => {
    const merged: ConsentFlags = { ...DEFAULT_FLAGS, ...flags, necessary: true }
    persist('custom', merged)
  }, [persist])

  const hasConsent = useCallback(
    (category: keyof ConsentFlags) => {
      if (!consentState) return category === 'necessary'
      return consentState.flags[category] ?? false
    },
    [consentState],
  )

  const resetConsent = useCallback(() => {
    localStorage.removeItem('owtli_cookie_consent')
    setConsentState(null)
  }, [])

  return {
    consentState,
    isLoading,
    showBanner: !isLoading && consentState === null,
    acceptAll,
    rejectAll,
    saveCustom,
    hasConsent,
    resetConsent,
  }
}
